import { useState, type ReactNode } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { AgencySignupSchema } from '@admitly/shared';
import { Route as rootRoute } from './__root.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Label } from '../components/ui/Label.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { ApiError } from '../lib/api.js';
import { submitAgencySignup } from '../lib/agency-signup.js';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/get-started',
  component: SignupAgencyPage,
});

/** Brand chrome shared with the login / forgot-password / signup pages. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3 justify-center">
          <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
            <span className="font-serif text-cream text-lg leading-none">A</span>
          </div>
          <div className="font-serif text-2xl tracking-tighter2 text-ink">Admitly</div>
        </div>
        <div className="bg-white border border-line rounded-2xl shadow-card p-8">{children}</div>
      </div>
    </div>
  );
}

/** Per-field validation messages, keyed by the schema field name. */
type FieldErrors = Partial<Record<string, string>>;

function SignupAgencyPage() {
  const [agencyName, setAgencyName] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);

    // Validate against the exact schema the API uses — single source of truth.
    const result = AgencySignupSchema.safeParse({
      agency_name: agencyName,
      admin_full_name: adminFullName,
      email,
      password,
      tos_accepted: tosAccepted,
      privacy_accepted: privacyAccepted,
    });
    if (!result.success) {
      const next: FieldErrors = {};
      for (const issue of result.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }

    setSubmitting(true);
    try {
      await submitAgencySignup(result.data);
      setSent(true);
    } catch (err) {
      // Surface only rate-limiting; any other error must NOT reveal whether the
      // email already exists — fall through to the same neutral confirmation.
      if (err instanceof ApiError && err.status === 429) {
        setFormError('Too many requests just now — please try again in a minute.');
      } else {
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">
            Check your email to verify your address
          </h1>
          <p className="text-sm text-muted mb-6">
            If that email is eligible, we&rsquo;ve sent a verification link. Click it to
            confirm your address, and we&rsquo;ll review your application from there.
          </p>
          <Link to="/login">
            <Button variant="secondary" className="w-full">
              Back to login
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-serif text-2xl text-ink tracking-tight mb-1">Get started with Admitly</h1>
      <p className="text-sm text-muted mb-6">
        Create your agency workspace. We&rsquo;ll verify your email, then review your
        application.
      </p>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <Label htmlFor="signup-agency-name">Agency name</Label>
          <Input
            id="signup-agency-name"
            autoComplete="organization"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            invalid={!!fieldErrors.agency_name}
            placeholder="Your agency"
          />
          {fieldErrors.agency_name && (
            <p className="text-xs text-danger mt-1.5">{fieldErrors.agency_name}</p>
          )}
        </div>

        <div>
          <Label htmlFor="signup-admin-name">Your full name</Label>
          <Input
            id="signup-admin-name"
            autoComplete="name"
            value={adminFullName}
            onChange={(e) => setAdminFullName(e.target.value)}
            invalid={!!fieldErrors.admin_full_name}
            placeholder="Your name"
          />
          {fieldErrors.admin_full_name && (
            <p className="text-xs text-danger mt-1.5">{fieldErrors.admin_full_name}</p>
          )}
        </div>

        <div>
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={!!fieldErrors.email}
            placeholder="you@agency.com.au"
          />
          {fieldErrors.email && <p className="text-xs text-danger mt-1.5">{fieldErrors.email}</p>}
        </div>

        <div>
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            invalid={!!fieldErrors.password}
            placeholder="••••••••••"
          />
          {fieldErrors.password ? (
            <p className="text-xs text-danger mt-1.5">{fieldErrors.password}</p>
          ) : (
            <p className="text-xs text-muted mt-1.5">At least 10 characters.</p>
          )}
        </div>

        <div>
          <Checkbox
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            label={
              <>
                I accept the{' '}
                <Link to="/terms" target="_blank" className="text-navy hover:underline">
                  Terms of Service
                </Link>
              </>
            }
          />
          {fieldErrors.tos_accepted && (
            <p className="text-xs text-danger mt-1.5">{fieldErrors.tos_accepted}</p>
          )}
        </div>

        <div>
          <Checkbox
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            label={
              <>
                I accept the{' '}
                <Link to="/privacy" target="_blank" className="text-navy hover:underline">
                  Privacy Policy
                </Link>
              </>
            }
          />
          {fieldErrors.privacy_accepted && (
            <p className="text-xs text-danger mt-1.5">{fieldErrors.privacy_accepted}</p>
          )}
        </div>

        {formError && (
          <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
            {formError}
          </div>
        )}

        <Button type="submit" size="lg" loading={submitting} className="w-full mt-2">
          Create my agency
        </Button>
      </form>

      <div className="mt-6 text-center text-xs text-muted">
        Already have an account?{' '}
        <Link to="/login" className="text-navy hover:underline">
          Log in
        </Link>
      </div>
    </Shell>
  );
}
